import { useFormContext, useWatch } from 'react-hook-form';
import { GWPCheckbox, GWPChoiceGroup, GWPField } from 'shared-components';
import ApplicationSection from '../ApplicationSection';
import { EntertainerApplicationContract, EntertainerApplicationFormState } from '../EntertainerApplicationTypes';
import { fieldError, useApplicationFormUpdate } from '../EntertainerApplicationFormUtils';

type Props = { contract: EntertainerApplicationContract };

export default function AvailabilitySection({ contract }: Props) {
	const { clearErrors, control, formState } = useFormContext<EntertainerApplicationFormState>();
	const update = useApplicationFormUpdate();
	const [availability, performanceType] = useWatch({
		control,
		name: ['EntertainerAvailability', 'EntertainerPerformanceType'],
	});
	const availabilityError = fieldError(formState.errors, 'EntertainerAvailability');

	return (
		<ApplicationSection
			title="Where and how you can perform"
			description="Choose every market or event you could reasonably attend."
		>
			<fieldset
				className={`entertainer-market-fieldset${availabilityError ? ' invalid' : ''}`}
				id="EntertainerAvailability"
				tabIndex={-1}
			>
				<legend>
					Market availability{' '}
					<span className="entertainer-required-mark" aria-hidden="true">
						*
					</span>
				</legend>
				<p>Paid positions are limited to markets marked below. Busking is available at every listed market.</p>
				<div className="entertainer-market-grid">
					{contract.markets.map((market) => (
						<GWPCheckbox
							key={market.id}
							checked={availability.includes(market.id)}
							onCheckedChange={(checked) => {
								update(
									'EntertainerAvailability',
									checked
										? [...availability, market.id]
										: availability.filter((id) => id !== market.id),
								);
								clearErrors('EntertainerPerformanceType');
							}}
							label={
								<span className="entertainer-market-label">
									<strong>{market.name}</strong>
									{market.supportsPaidPerformance ? (
										<small className="entertainer-market-badge">Paid</small>
									) : null}
								</span>
							}
						/>
					))}
				</div>
				{availabilityError ? (
					<p className="entertainer-field-error" role="alert">
						{availabilityError}
					</p>
				) : null}
			</fieldset>
			<GWPField
				label="Performance preference"
				required
				hint="Tell us which opportunities you would like to hear about"
				error={fieldError(formState.errors, 'EntertainerPerformanceType')}
			>
				<GWPChoiceGroup
					id="EntertainerPerformanceType"
					tabIndex={-1}
					name="EntertainerPerformanceType"
					orientation="horizontal"
					value={performanceType}
					options={contract.performancePreferences.map((preference) => ({
						value: String(preference.value),
						label: preference.label,
						description: preference.description,
					}))}
					onValueChange={(value) => update('EntertainerPerformanceType', value)}
				/>
			</GWPField>
		</ApplicationSection>
	);
}
